import { assign, createMachine } from "xstate";

import isPersonalEmail from "@brexhq/next/src/utils/isPersonalEmail";
import { LeadFormStatus } from "@brexhq/next/types/brex-api";

import {
  isESTier1,
  isESTier2,
  isGrowthTier1Plus,
  isGrowthTier2,
  isSmallBusinessSegment,
  isReferralPartner,
  sameWorkEmail,
  isEnterprise,
} from "./helpers";
import {
  segESTierTwoSubMachine,
  segGrowthTier2SubMachine,
  segSBSubMachine,
  segUnidentifiedSubMachine,
} from "./subMachines";
import {
  leadFormEvents,
  leadFormMachineContext,
  leadFormMachineEvent,
  leadFormMachineState,
  LeadFormSteps,
} from "./types";

/**
 * the 3 different flows we're sending to users, based on 3p data
 */
const existingLeadFlow = [
  {
    target: LeadFormSteps.segSB,
    actions: ["updateFields", "clearError"],
    cond: (ctx, event) => {
      return (
        sameWorkEmail(ctx.workEmail, event.value?.workEmail) &&
        !!ctx.salesSegment &&
        isSmallBusinessSegment(ctx.salesSegment)
      );
    },
  },
  {
    target: LeadFormSteps.segESTierTwo,
    actions: ["updateFields", "clearError"],
    cond: (ctx, event) => {
      return (
        sameWorkEmail(ctx.workEmail, event.value?.workEmail) &&
        !!ctx.salesSegment &&
        isESTier2(ctx.salesSegment)
      );
    },
  },
  {
    target: LeadFormSteps.segGrowthTier2,
    actions: ["updateFields", "clearError"],
    cond: (ctx, event) => {
      return (
        sameWorkEmail(ctx.workEmail, event.value?.workEmail) &&
        !!ctx.salesSegment &&
        isGrowthTier2(ctx.salesSegment)
      );
    },
  },
  {
    target: LeadFormSteps.segUnidentified,
    actions: ["updateFields", "clearError"],
    cond: (ctx, event) => {
      return (
        sameWorkEmail(ctx.workEmail, event.value?.workEmail) &&
        !ctx.salesSegment
      );
    },
  },
];

const assignPostCreateLead = (_, event) => {
  return {
    leadToken: event.data.leadToken,
    salesSegment: event.data.salesSegment,
    id: event.data.id,
    refCode: event.data.refCode,
  };
};

export const obs4Machine = createMachine<
  leadFormMachineContext,
  leadFormMachineEvent,
  leadFormMachineState
>(
  {
    id: "leadFormV2",
    initial: LeadFormSteps.userInfo,
    states: {
      [LeadFormSteps.userInfo]: {
        id: LeadFormSteps.userInfo,
        on: {
          [leadFormEvents.submit]: [
            {
              target: LeadFormSteps.website,
              actions: ["updateFields", "clearError"],
              cond: (_, event) => isPersonalEmail(event.value.workEmail || ""),
            },
            /**
             * initial round of checks, to make sure we don't create another lead
             * when users go back to email during a single session
             */
            ...existingLeadFlow,
            {
              target: LeadFormSteps.createLead,
              actions: ["updateFields", "clearError"],
            },
          ],
        },
      },
      [LeadFormSteps.website]: {
        id: LeadFormSteps.website,
        on: {
          [leadFormEvents.submit]: [
            {
              target: LeadFormSteps.createLead,
              actions: ["updateFields"],
            },
          ],
          [leadFormEvents.back]: [{ target: LeadFormSteps.userInfo }],
        },
      },
      [LeadFormSteps.createLead]: {
        invoke: {
          src: "createLeadMutation",
          onDone: [
            {
              target: LeadFormSteps.waitlist,
              cond: (_, event) => {
                return event.data.leadStatus === LeadFormStatus.BAD_LEAD;
              },
            },
            {
              target: LeadFormSteps.success,
              cond: (context, event) => {
                return (
                  isEnterprise(event.data.salesSegment) &&
                  !isPersonalEmail(context.workEmail) &&
                  !isReferralPartner(context.refCode)
                );
              },
              actions: assign(assignPostCreateLead),
            },
            {
              target: LeadFormSteps.segGrowthTier2,
              cond: (_, event) => {
                return isGrowthTier2(event.data.salesSegment);
              },
              actions: assign(assignPostCreateLead),
            },
            {
              target: LeadFormSteps.segESTierTwo,
              cond: (_, event) => {
                return isESTier2(event.data.salesSegment);
              },
              actions: assign(assignPostCreateLead),
            },
            {
              target: LeadFormSteps.segSB,
              cond: (_, event) => {
                return isSmallBusinessSegment(event.data.salesSegment);
              },
              actions: assign(assignPostCreateLead),
            },
            {
              target: LeadFormSteps.password,
              cond: (_, event) => {
                return (
                  isESTier1(event.data.salesSegment) ||
                  isGrowthTier1Plus(event.data.salesSegment)
                );
              },
              actions: assign(assignPostCreateLead),
            },
            {
              // Fall through to the un segmented flow just in case
              target: LeadFormSteps.segUnidentified,
              actions: assign(assignPostCreateLead),
            },
          ],
          onError: {
            target: LeadFormSteps.userInfo,
            actions: assign({
              errorMessage: (_, event) => {
                return event.data;
              },
            }),
          },
        },
      },
      [LeadFormSteps.segSB]: {
        ...segSBSubMachine,
      },
      [LeadFormSteps.segUnidentified]: {
        ...segUnidentifiedSubMachine,
      },
      [LeadFormSteps.segESTierTwo]: {
        ...segESTierTwoSubMachine,
      },
      [LeadFormSteps.segGrowthTier2]: {
        ...segGrowthTier2SubMachine,
      },
      [LeadFormSteps.password]: {
        id: LeadFormSteps.password,
        on: {
          [leadFormEvents.submit]: [
            {
              target: LeadFormSteps.signup,
              actions: ["updateFields"],
            },
          ],
        },
      },
      [LeadFormSteps.signup]: {
        invoke: {
          src: "softSignupV2Mutation",
          onError: {
            target: LeadFormSteps.password,
            actions: assign({
              errorMessage: (_, event) => {
                return event.data;
              },
            }),
          },
        },
      },
      [LeadFormSteps.waitlist]: {
        id: LeadFormSteps.waitlist,
        type: "final",
      },
      [LeadFormSteps.success]: {
        id: LeadFormSteps.success,
        type: "final",
      },
    },

    on: {
      [leadFormEvents.updateContext]: {
        actions: assign((ctx, evt) => ({
          ...ctx,
          ...evt.value,
        })),
        internal: true,
      },
    },
  },
  {
    actions: {
      updateFields: assign((_, event) => {
        if (event.type === leadFormEvents.submit) {
          return { ...event.value };
        }
        return {};
      }),
      clearError: assign({
        errorMessage: undefined,
      }),
    },
  },
);
